/**
 * Read surface — shared types (defined ONCE here; UI kit + routes import from here).
 *
 * The Read surface (global `/read/` + chain `/<chain>/read/`) is a merged, multi-source
 * index over articles (news/analysis/guides), breaking news, and daily/weekly recaps.
 * A `ReadItem` is the normalized, RSC-serializable shape every source maps into; every
 * `href` is a trailing-slash detail URL that JUMPS to the existing canonical reader.
 */
import type { ChainId } from '@/lib/chains';

export type ReadItemType =
  | 'breaking'
  | 'news'
  | 'analysis'
  | 'guide'
  | 'daily'
  | 'weekly'
  | 'edge';

/**
 * One resolved entity reference (gold-linked chip / inline link). Built by the
 * server from a tagId or a recap @handle token against the active tag set.
 */
export interface ReadEntityRef {
  slug: string;
  name: string;
  /** Optional avatar (primary tag image) — used by brief chips. */
  image?: string;
}

/**
 * Chain-of-custody provenance for a breaking item (the source tweet it was
 * intercepted from). Present only when the breaking doc carries a `tweetUrl`.
 */
export interface ReadSourceTweet {
  /** The source tweet's @username (display case, no leading @). */
  handle: string;
  /** Permalink to the source tweet (breaking_news.tweetUrl). */
  url: string;
  /** ISO 8601 — when the source tweet was posted (breaking_news.sourceTweetDate). */
  interceptedAt?: string;
}

/**
 * One source citation on an EDGE item (projected from `RelationshipSource`). The
 * snippet/url drive the expandable edge panel's "SRC n" rows; `method` is the
 * human-readable verification badge. Client-safe (no asserter uids leaked).
 */
export interface ReadEdgeSource {
  /** Quoted evidence text (RelationshipSource.snippet), trimmed. */
  snippet?: string;
  /** Source permalink (RelationshipSource.url). */
  url?: string;
  /** Verification-method badge label ('self · attested', 'news', 'on-chain', …). */
  method: string;
}

/**
 * Structured payload carried by an EDGE item (a confirmed graph relationship,
 * surfaced as a first-class wire kind). Projected server-side from an
 * `EntityRelationship` doc — the from/to dossier links, the typed verb, the
 * computed confidence, and the (firewall-passed) source citations.
 */
export interface ReadEdge {
  /** Source-entity display name + dossier href. */
  fromName: string;
  fromSlug: string;
  fromHref: string;
  /** Target-entity display name + dossier href. */
  toName: string;
  toSlug: string;
  toHref: string;
  /** Raw relationship type for the mono "—PARTNERED_WITH→" verb (e.g. 'PARTNER_OF'). */
  relType: string;
  /** Human verb label ('partner of', 'developed by', …). */
  verb: string;
  /** Computed/overridden confidence label ('high' | 'medium' | 'low'). */
  confidence: string;
  /** Free-text narrative (EntityRelationship.description), when present. */
  description?: string;
  /** Firewall-passed source citations (non-sponsored edges only). */
  sources: ReadEdgeSource[];
}

/**
 * Structured payload carried by a DAILY recap item (the day's brief). Sourced
 * verbatim from the `ecosystem_recaps` doc — `highlights` (string bullets with
 * inline @handle/$token tokens), `sentiment`, `sourceCounts` (provenance), and
 * the resolved entities mentioned (for the gold chip row).
 */
export interface ReadRecapBrief {
  /** 0-5 structured highlight bullets (recap.highlights), tokens stripped/linked in UI. */
  highlights: string[];
  /** Day mood — recap.sentiment ('bullish' | 'bearish' | 'mixed' | …). */
  sentiment?: string;
  /** Provenance counts (recap.sourceCounts) for the "compiled by the desk" footer. */
  sourceCounts?: {
    activityPulses?: number;
    breakingNews?: number;
    topTweets?: number;
    total?: number;
  };
  /** Resolved entities mentioned in the recap (recap.mentionedEntities → tags). */
  entities: ReadEntityRef[];
}

export interface ReadItem {
  id: string;
  type: ReadItemType;
  title: string;
  /** Detail URL with trailing slash (jumps to the canonical reader). */
  href: string;
  excerpt?: string;
  image?: string;
  /** ISO 8601 publish timestamp (used for the merged newest-first sort). */
  publishedAt: string;
  /** Calendar day this item belongs to (YYYY-MM-DD, UTC) — the wire grouping key. */
  dateKey?: string;
  author?: string;
  /** Breaking only: the source tweet's @username (no leading @). */
  sourceHandle?: string;
  /** Breaking only: chain-of-custody source tweet (when `tweetUrl` is present). */
  sourceTweet?: ReadSourceTweet;
  /** Resolved entity references (from tagIds) — drives inline gold chips on rows. */
  entities?: ReadEntityRef[];
  /** Daily-recap only: the structured brief payload (highlights/sentiment/provenance). */
  recap?: ReadRecapBrief;
  /** Edge only: the structured relationship payload (from/to/verb/confidence/sources). */
  edge?: ReadEdge;
  tagIds: string[];
  /**
   * Slugs of every entity this item references — the server-side `?entity=` filter
   * key. Built from resolved `entities`/`recap.entities`/`edge` endpoints so a
   * filtered wire and the dossier timeline stay one system.
   */
  entitySlugs?: string[];
}

/** One calendar day on the wire: its brief (daily recap) + its dense rows. */
export interface ReadDay {
  /** YYYY-MM-DD (UTC). */
  date: string;
  /** The day's daily-recap brief, if one exists for the day. */
  brief?: ReadItem;
  /** Every other item that day (breaking · news · analysis · guide · weekly), newest-first. */
  items: ReadItem[];
  /** Total renderable count (brief + items) — drives the band "· N items". */
  count: number;
}

export interface ReadData {
  /** 0-3 curated lead items (featured location → bumped breaking → newest). */
  featured: ReadItem[];
  /**
   * The latest published daily recap, surfaced separately so the Read surface can
   * promote it to a full-width hero in the curated top row (the editorial centerpiece).
   * Undefined when no recap is available for this scope. De-duped out of `items`.
   */
  recapHero?: ReadItem;
  /** Merged feed, publishedAt desc, with `featured` ids de-duped out. */
  items: ReadItem[];
  /** Per-type counts of `items` (drives the filter chips). */
  counts: Record<ReadItemType, number>;
}

/**
 * Resolved `?entity=` filter context (server-side). Present only when the wire is
 * scoped to a single entity; drives the gold filter bar + the canonical URL.
 */
export interface WireEntityFilter {
  /** The entity's slug (the `?entity=` value). */
  slug: string;
  /** Display name for the filter bar. */
  name: string;
  /** Canonical dossier href (`/tags/<slug>/`). */
  dossierHref: string;
  /** Total items in view after the filter (brief + rows across all days). */
  count: number;
}

/**
 * The "published since today's brief" divider context: the publishedAt of today's
 * daily recap. The UI drops a thin divider before today's rows that published
 * AFTER this instant. Present only when today carries a brief.
 */
export interface SinceBriefMarker {
  /** UTC calendar day (YYYY-MM-DD) the marker applies to (today). */
  dateKey: string;
  /** ISO publishedAt of today's brief — rows published after it sit above the divider. */
  briefPublishedAt: string;
}

/**
 * The day-grouped wire payload (THE WIRE). Built by `getWireData` on top of the
 * same multi-source aggregation as `ReadData`; days are newest-first, each with
 * its brief + dense rows. `counts` drives the sticky filter chips.
 */
export interface WireData {
  /** Calendar days, newest-first, each carrying its brief + rows. */
  days: ReadDay[];
  /** Per-type counts across the whole wire (drives the filter chips). */
  counts: Record<ReadItemType, number>;
  /** Resolved `?entity=` filter context, when the wire is entity-scoped. */
  entityFilter?: WireEntityFilter;
  /** Today's brief-publishedAt marker, when today carries a recap. */
  sinceBrief?: SinceBriefMarker;
}

/** Display labels for chips/badges, keyed by item type. */
export const READ_TYPE_LABEL: Record<ReadItemType, string> = {
  breaking: 'Breaking',
  news: 'News',
  analysis: 'Analysis',
  guide: 'Guide',
  daily: 'Daily recap',
  weekly: 'Weekly recap',
  edge: 'Edge',
};

/** Aggregator options. Omit `chainId` for the global (all-ecosystems) feed. */
export interface GetReadDataOptions {
  chainId?: ChainId;
}

/**
 * Wire options. `chainId` scopes to one ecosystem (omit for global). `date`
 * (YYYY-MM-DD) filters the wire to a single calendar day for the dated permalink
 * route — filtered in-memory over the existing capped queries (no new index).
 * `entity` (a slug) filters the wire to items referencing that entity (the
 * crawlable `?entity=` state) — server-side so the filtered page is indexable.
 */
export interface GetWireDataOptions {
  chainId?: ChainId;
  date?: string;
  entity?: string;
}
